/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable, Injector, NgZone } from '@angular/core';

import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BuilderUserContext, BuilderUserData, ThemeService } from '@gh/builder-shared';

import { NavigationService, PageEnvironmentManager } from '@gh/common-internal-shared';
import { ICONS_URL } from '@gh/common-shared';
import { DEFAULT_LOCALE, VERSION } from '@gh/config';

import { AuthProvider, AuthService, AuthState } from '@gh/core-auth';
import { Message, MessageBasket, MessageSeverity } from '@gh/core-messages';
import { Locale, TranslationMapLoader } from '@gh/core-mls';
import { TypeService } from '@gh/core-type';
import { DialogService } from '@gh/core-ui';

import { firstValueFrom } from 'rxjs';

import { getBuilderBootstrapParams } from '../builder-app.utils';
import { licenseAgreementDialogRoute } from '../components/license-agreement-dialog/license-agreement-dialog.component';

import { buildBuilderNavigationMenu } from './builder-navigation-config';

const DEFAULT_THEME_NAME = 'blue';

@Injectable()
export class BuilderBootstrapService {
  constructor(private injector: Injector,
    private sanitizer: DomSanitizer,
    private navigation: NavigationService,
    private translationLoader: TranslationMapLoader,
    private environmentManager: PageEnvironmentManager,
    private userContext: BuilderUserContext,
    private authService: AuthService,
    private authProvider: AuthProvider,
    private iconRegistry: MatIconRegistry,
    private ngZone: NgZone) {
    const iconsUrlWithCacheBust = `${ICONS_URL}?v=${VERSION}`;
    iconRegistry.addSvgIconSetInNamespace('gh', sanitizer.bypassSecurityTrustResourceUrl(iconsUrlWithCacheBust));
    this.environmentManager.update();

    this.authService.onAuthState.subscribe((authState) => {
      if (authState !== AuthState.LoggedIn) {
        this.setPageStatus('is-app-wait-for-login');
      }
    });

    // set this service on global object to be available on the page (for index.html)
    window['bootstrap'] = this;
  }

  get dialogService(): DialogService {
    return this.injector.get(DialogService);
  }

  get themeService(): ThemeService {
    return this.injector.get(ThemeService);
  }

  get router(): Router {
    return this.injector.get(Router);
  }

  login(): Promise<any> {
    // we use here NgZone, because this method can be called outside of angular zone
    return this.ngZone.run(() => this.authProvider.login());
  }

  load(): Promise<any> {
    // Load the default theme before the user context: dialogs opened during login (e.g. the office picker)
    // render before the user's color scheme is known and would otherwise have no Material theme at all
    // (transparent select panels, no typography). The user's own scheme replaces it below.
    const defaultTheme$ = this.themeService.load(DEFAULT_THEME_NAME).then(() => this.themeService.use(DEFAULT_THEME_NAME));
    return defaultTheme$
      .then(() => firstValueFrom(this.userContext.init(getBuilderBootstrapParams())).then((userData) => {
        this.loadNavigation(this.userContext);

        return Promise.all([
          this.loadTranslation(userData!.locale),
        ]).then(() => userData);
      })).then((userData) => {
        const { colorScheme } = this.userContext;
        return this.themeService.load(colorScheme).then(() => {
          this.themeService.use(colorScheme);
          return userData;
        });
      }).then((userData: BuilderUserData) => {
        if (!userData.hasUnacceptedAgreements) {
          return Promise.resolve(userData);
        } else {
          return firstValueFrom(this.dialogService.openWithInput(licenseAgreementDialogRoute, { viewMode: false })
            .afterClosed())
            .then((accepted) => {
              if (accepted) {
                return Promise.resolve({ ...userData, isLicenseAccepted: true });
              } else {
                return firstValueFrom(this.userContext.logout())
                  .then(() => Promise.reject(new Error('BuilderBootstrapService: License was rejected')));
              }
            });
        }
      }).then((userData) => {
        // init types after localization subsystem is ready
        this.injector.get(TypeService);
        this.setPageStatus('is-app-loaded');

        return userData;
      }).catch((error) => {
        if (error instanceof Error) {
          return Promise.reject(error);
        }

        return Promise.all([
          this.themeService.load(DEFAULT_THEME_NAME).then(() => this.themeService.use(DEFAULT_THEME_NAME)),
          this.loadTranslation(Locale.createFromStandard(DEFAULT_LOCALE)),
        ]).then(() => {
          if (error instanceof MessageBasket) {
            const message = error.at(0) || Message.error('msg_internalservererror');
            this.dialogService.message({
              severity: MessageSeverity.Error,
              messageId: message.key,
              messageParams: message.params,
              disableClose: true,
            }).subscribe(() => this.userContext.logout());
          }

          return Promise.resolve();
        });
      });
  }

  private loadNavigation(userContext: BuilderUserContext): void {
    buildBuilderNavigationMenu(userContext, this.injector).forEach((item) => this.navigation.addItemTree(item));
  }

  private loadTranslation(locale: Locale): Promise<any> {
    return this.translationLoader.load(locale);
  }

  private setPageStatus(className: string): void {
    document.body.classList.remove('is-app-loading', 'is-app-wait-for-login', 'is-app-loaded');
    document.body.classList.add(className);
  }
}
